import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type DEANMode = 'cubicle' | 'phone';

export interface DEANMessage {
  id: string;
  role: 'coach' | 'dean';
  content: string;
  timestamp: Date;
}

interface DEANContextType {
  // Panel state
  isOpen: boolean;
  isDocked: boolean;
  mode: DEANMode;

  // Chat state
  messages: DEANMessage[];
  isLoading: boolean;
  error: string | null;

  // Client context
  clientId: string | null;
  clientName: string | null;

  // Actions
  openPanel: (clientId: string, clientName: string) => void;
  closePanel: () => void;
  toggleDocked: () => void;
  setMode: (mode: DEANMode) => void;
  sendMessage: (message: string) => Promise<void>;
  clearChat: () => void;
}

const DEANContext = createContext<DEANContextType | null>(null);

export const useDEAN = () => {
  const context = useContext(DEANContext);
  if (!context) {
    throw new Error('useDEAN must be used within a DEANProvider');
  }
  return context;
};

interface DEANProviderProps {
  children: ReactNode;
}

export const DEANProvider: React.FC<DEANProviderProps> = ({ children }) => {
  // Panel state
  const [isOpen, setIsOpen] = useState(false);
  const [isDocked, setIsDocked] = useState(true);
  const [mode, setMode] = useState<DEANMode>('cubicle');

  // Chat state
  const [messages, setMessages] = useState<DEANMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Client context
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);

  const openPanel = useCallback((cId: string, cName: string) => {
    // If switching clients, clear chat
    if (cId !== clientId) {
      setMessages([]);
      setError(null);
    }
    setClientId(cId);
    setClientName(cName);
    setIsOpen(true);
  }, [clientId]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleDocked = useCallback(() => {
    setIsDocked(prev => !prev);
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    if (!clientId || !message.trim()) return;

    const coachMessage: DEANMessage = {
      id: `msg-${Date.now()}-coach`,
      role: 'coach',
      content: message.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, coachMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/dean/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          clientId,
          message: message.trim(),
          mode,
          history: messages.map(m => ({
            role: m.role,
            content: m.content
          }))
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response from DEAN');
      }

      const data = await response.json();

      const deanMessage: DEANMessage = {
        id: `msg-${Date.now()}-dean`,
        role: 'dean',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, deanMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, mode, messages]);

  const value: DEANContextType = {
    isOpen,
    isDocked,
    mode,
    messages,
    isLoading,
    error,
    clientId,
    clientName,
    openPanel,
    closePanel,
    toggleDocked,
    setMode,
    sendMessage,
    clearChat
  };

  return (
    <DEANContext.Provider value={value}>
      {children}
    </DEANContext.Provider>
  );
};
